import { ContentInsertVariables } from 'api/graphQL/types/contentInsert.types'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

/** Default state for copy/paste dialog */
const defaultCopyPasteState = {
    showPasteDialog: false,
    contentInsertVariables: null as Partial<ContentInsertVariables> | null,
}

export type CopyPasteState = typeof defaultCopyPasteState & {
    openPasteDialog: (vars: Partial<ContentInsertVariables>) => void
    resetCopyPasteState: () => void
}

export const useCopyPasteStore = create<CopyPasteState>((set) => ({
    ...defaultCopyPasteState,

    openPasteDialog: (vars) => {
        set({
            showPasteDialog: true,
            contentInsertVariables: vars,
        })
    },
    resetCopyPasteState: () => set(defaultCopyPasteState),
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `useCopyPasteStore` store.
 * Sample usage:
 * ```typescript
 * const { showPasteDialog, openPasteDialog } = useCopyPasteShallowSelector ((state) => ({
 *   showPasteDialog: state.showPasteDialog,
 *   openPasteDialog: state.openPasteDialog,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useCopyPasteShallowSelector = <T>(
    selector: (state: CopyPasteState) => T
): T => useCopyPasteStore(useShallow(selector))
